import React, { createContext, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useBrazeSDK } from '@lyrahealth-inc/shared-app-logic/src/features/braze/useBrazeSDK'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'

import { getBrazeHashedLyraId, getIsDetectedCountryInternational } from '../../data/appGlobals/appGlobalsSelectors'
import { getIsUserInternational, getIsUserLoggedIn } from '../../data/user/userSelectors'

export interface BrazeContextValue {
  isReady: boolean
  logCustomEvent: (event: string, properties?: Record<string, any>) => void
}

const BrazeContext = createContext<BrazeContextValue>({ isReady: false, logCustomEvent: () => {} })

export const useBraze = (): BrazeContextValue => {
  return useContext(BrazeContext)
}

/**
 * Braze Provider that handles SDK loading.
 */
export const BrazeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isBrazeEnabled: isBrazeEnabledFlag } = useFlags()
  const hashedLyraId = useSelector(getBrazeHashedLyraId)
  let isInternational = useSelector(getIsDetectedCountryInternational)
  const isUserInternational = useSelector(getIsUserInternational)
  const isUserLoggedIn = useSelector(getIsUserLoggedIn)
  isInternational = isUserLoggedIn ? isUserInternational : isInternational
  const isBrazeEnabled = isBrazeEnabledFlag && !isInternational
  const { isReady, logCustomEvent } = useBrazeSDK(isBrazeEnabled, hashedLyraId)
  const value = useMemo(() => ({ isReady, logCustomEvent }), [isReady, logCustomEvent])

  return <BrazeContext.Provider value={value}>{children}</BrazeContext.Provider>
}

export default BrazeProvider
